import React, { useState, useEffect } from "react";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Textarea } from "../components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import { Card, CardContent, CardHeader, CardTitle } from "../components/ui/card";
import { ArrowLeft, Save, Send, Eye, AlertCircle } from "lucide-react";
import { Link, useNavigate, useParams } from "react-router-dom";
import { useAuth } from "../context/AuthContext";
import { useData } from "../context/DataContext";
import { toast } from "sonner";
import { Memo } from "../types";

export default function MemoEditPage() {
  const { id } = useParams<{ id: string }>();
  const { currentUser } = useAuth();
  const { users, memos, updateMemo, approvers, fetchApprovers } = useData();
  const navigate = useNavigate();

  const [formData, setFormData] = useState({
    number: "",
    subject: "",
    effectiveDate: "",
    expiryDate: "",
    approvedBy: "",
    requireSignature: false
  });

  const [content, setContent] = useState("");
  const [errors, setErrors] = useState<Record<string, string>>({});
  const [previewMode, setPreviewMode] = useState(false);
  const [isProcessing, setIsProcessing] = useState(false);
  const [isLoading, setIsLoading] = useState(true);
  const [memo, setMemo] = useState<Memo | null>(null);

  // AI Enhancement State
  const [aiState, setAiState] = useState({
    isLoading: false,
    showPreview: false,
    currentField: '',
    currentAction: '',
    result: '',
    originalValue: ''
  });

  // Fetch memo directly from backend
  const fetchMemo = async (memoId: string) => {
    try {
      const API_BASE = process.env.REACT_APP_API_BASE_URL || "http://localhost:5001";
      const response = await fetch(`${API_BASE}/api/memos/${memoId}`, {
        headers: {
          Authorization: `Bearer ${localStorage.getItem('token')}`,
          'Content-Type': 'application/json'
        }
      });

      if (!response.ok) {
        throw new Error('Failed to fetch memo');
      }

      const data = await response.json();
      if (data.success && data.memo) {
        const memo = data.memo;
        setMemo(memo);

        // Block editing of active memos
        if (memo.status === 'Active') {
          toast.error('Cannot edit active memos. Please duplicate the memo to create a new version.');
          navigate(`/memo-view/${memoId}`);
          return;
        }

        setFormData({
          number: memo.number || "",
          subject: memo.subject || "",
          effectiveDate: memo.effectiveDate?.split('T')[0] || "",
          expiryDate: memo.expiryDate?.split('T')[0] || "",
          approvedBy: memo.approvedBy?.id || "",
          requireSignature: !!memo.requireSignature
        });

        setContent(memo.content || "");
      }
    } catch (error) {
      console.error('Error fetching memo:', error);
      toast.error('Failed to fetch memo details');
      navigate('/policies');
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    if (approvers.length === 0) {
      fetchApprovers();
    }

    if (id) {
      fetchMemo(id);
    }
  }, [id, currentUser, navigate]);

  // Handle effective date change and clear dependent dates if needed
  const handleEffectiveDateChange = (newEffectiveDate: string) => {
    const updatedFormData = { ...formData, effectiveDate: newEffectiveDate };

    // If effective date is cleared, clear expiry date
    if (!newEffectiveDate) {
      updatedFormData.expiryDate = "";
    } else {
      // If new effective date is after current expiry date, clear it
      if (formData.expiryDate && new Date(formData.expiryDate) <= new Date(newEffectiveDate)) {
        updatedFormData.expiryDate = "";
      }
    }

    setFormData(updatedFormData);

    // Clear related errors if they exist
    if (errors.expiryDate) {
      const newErrors = { ...errors };
      delete newErrors.expiryDate;
      setErrors(newErrors);
    }
  };

  // Filter users for approvers (Admin, Director, Manager)
  const getEligibleApprovers = () => {
    return users.filter(user =>
      !user.archived
    );
  };

  const handleAiAccept = () => {
    const { currentField, result } = aiState;

    switch (currentField) {
      case 'subject':
        setFormData({ ...formData, subject: result });
        break;
      case 'content':
        setContent(result);
        break;
    }

    setAiState({
      isLoading: false,
      showPreview: false,
      currentField: '',
      currentAction: '',
      result: '',
      originalValue: ''
    });

    toast.success('Enhancement applied successfully!');
  };

  const handleAiCancel = () => {
    setAiState({
      isLoading: false,
      showPreview: false,
      currentField: '',
      currentAction: '',
      result: '',
      originalValue: ''
    });
  };

  const validateForm = (isDraft = false) => {
    const newErrors: Record<string, string> = {};

    // Required fields for both draft and published memos
    if (!formData.subject.trim()) newErrors.subject = "Subject is required";
    if (!content.trim()) newErrors.content = "Content is required";
    if (!formData.effectiveDate) newErrors.effectiveDate = "Effective date is required";

    // Additional validation for published memos
    if (!isDraft) {
      if (!formData.approvedBy) newErrors.approvedBy = "Approver is required for published memos";
    }

    // Date validations
    if (formData.effectiveDate && formData.expiryDate) {
      if (new Date(formData.expiryDate) <= new Date(formData.effectiveDate)) {
        newErrors.expiryDate = "Expiry date must be after effective date";
      }
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSave = async (status: 'Draft' | 'Active') => {
    const isValid = validateForm(status === 'Draft');
    if (!isValid || !currentUser || !id) return;

    setIsProcessing(true);
    try {
      const updatedMemo = {
        number: formData.number,
        subject: formData.subject,
        content: content,
        effectiveDate: formData.effectiveDate,
        expiryDate: formData.expiryDate || undefined,
        approvedBy: formData.approvedBy || undefined,
        requireSignature: formData.requireSignature,
        status: status
      };

      await updateMemo(id, updatedMemo);

      toast.success(`Memo ${status === 'Draft' ? 'updated' : 'published'} successfully!`);
      navigate('/policies?tab=memos');
    } catch (error) {
      console.error(`Failed to ${status === 'Draft' ? 'update' : 'publish'} memo:`, error);
      toast.error(`Failed to ${status === 'Draft' ? 'update' : 'publish'} memo`);
    } finally {
      setIsProcessing(false);
    }
  };

  if (isLoading) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <div className="animate-spin rounded-full h-32 w-32 border-b-2 border-blue-600"></div>
      </div>
    );
  }

  // const memo = memos.find(m => m.id == id);
  if (!memo) {
    return (
      <div className="space-y-6">
        <div className="text-center py-8">
          <AlertCircle className="h-12 w-12 text-red-500 mx-auto mb-4" />
          <h3 className="text-lg font-medium mb-2">Memo not found</h3>
          <p className="text-muted-foreground">The memo you're looking for doesn't exist.</p>
          <Button onClick={() => navigate('/policies')} className="mt-4">
            Back to Policies
          </Button>
        </div>
      </div>
    );
  }

  if (previewMode) {
    return (
      <div className="space-y-6">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-4">
            <Button variant="outline" size="icon" onClick={() => setPreviewMode(false)}>
              <ArrowLeft className="h-4 w-4" />
            </Button>
            <div>
              <h1 className="text-2xl font-bold">Preview: {formData.subject || "Untitled Memo"}</h1>
              <p className="text-muted-foreground">Preview how this memo will appear to users</p>
            </div>
          </div>
          <div className="flex gap-2">
            <Button variant="outline" onClick={() => setPreviewMode(false)}>
              Back to Edit
            </Button>
            <Button variant="outline" onClick={() => handleSave('Draft')} disabled={isProcessing}>
              <Save className="h-4 w-4 mr-2" />
              {isProcessing ? "Saving..." : "Save as Draft"}
            </Button>
            <Button onClick={() => handleSave('Active')} disabled={isProcessing}>
              <Send className="h-4 w-4 mr-2" />
              {isProcessing ? "Publishing..." : "Save & Publish"}
            </Button>
          </div>
        </div>

        <Card>
          <CardHeader>
            <div className="flex items-center justify-between">
              <div>
                <CardTitle>{formData.subject || "Untitled Memo"}</CardTitle>
                <p className="text-muted-foreground">
                  {formData.number} • Version {memo.version || 1} •
                  Effective: {formData.effectiveDate ? new Date(formData.effectiveDate).toLocaleDateString() : "Not set"}
                </p>
              </div>
            </div>
          </CardHeader>
          <CardContent className="space-y-6">
            <div className="prose max-w-none">
              <div className="whitespace-pre-wrap">
                {content || "No content provided"}
              </div>
            </div>
          </CardContent>
        </Card>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-4">
          <Link to="/policies">
            <Button variant="outline" size="icon">
              <ArrowLeft className="h-4 w-4" />
            </Button>
          </Link>
          <div className="flex items-center gap-3">
            <div>
              <h1 className="text-2xl font-bold">Edit Memo</h1>
              <p className="text-muted-foreground">
                {formData.number} • Version {memo.version || 1} •
                Status: {memo.status}
              </p>
            </div>
            <div className="flex items-center gap-2">
              <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-blue-100 text-blue-800">
                v{memo.version || 1}
              </span>
              <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${memo.status === 'Draft'
                ? 'bg-orange-100 text-orange-800'
                : 'bg-green-100 text-green-800'
                }`}>
                {memo.status}
              </span>
            </div>
          </div>
        </div>

        <div className="flex gap-2">
          <Button variant="outline" onClick={() => setPreviewMode(true)}>
            <Eye className="h-4 w-4 mr-2" />
            Preview
          </Button>
          <Button variant="outline" onClick={() => handleSave('Draft')} disabled={isProcessing}>
            <Save className="h-4 w-4 mr-2" />
            {isProcessing ? "Saving..." : "Save as Draft"}
          </Button>
          <Button onClick={() => handleSave('Active')} disabled={isProcessing}>
            <Send className="h-4 w-4 mr-2" />
            {isProcessing ? "Publishing..." : "Save & Publish"}
          </Button>
        </div>
      </div>

      <div className="grid gap-6 lg:grid-cols-3">
        {/* Main Form */}
        <div className="lg:col-span-2 space-y-6">
          <Card>
            <CardHeader>
              <CardTitle>Memo Information</CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label htmlFor="number">Memo Number</Label>
                  <Input
                    id="number"
                    value={formData.number}
                    onChange={(e) => setFormData({ ...formData, number: e.target.value })}
                    placeholder="Auto-generated"
                    disabled
                  />
                </div>

                <div className="space-y-2">
                  <Label htmlFor="effectiveDate">Effective Date *</Label>
                  <Input
                    id="effectiveDate"
                    type="date"
                    value={formData.effectiveDate}
                    onChange={(e) => handleEffectiveDateChange(e.target.value)}
                    className={errors.effectiveDate ? "border-red-500" : ""}
                  />
                  {errors.effectiveDate && <p className="text-sm text-red-500">{errors.effectiveDate}</p>}
                </div>
              </div>

              <div className="space-y-2">
                <Label htmlFor="subject">Subject *</Label>
                <Input
                  id="subject"
                  value={formData.subject}
                  onChange={(e) => setFormData({ ...formData, subject: e.target.value })}
                  placeholder="Enter memo subject"
                  className={errors.subject ? "border-red-500" : ""}
                />
                {errors.subject && <p className="text-sm text-red-500">{errors.subject}</p>}
              </div>

              <div className="space-y-2">
                <Label htmlFor="expiryDate">Expiry Date</Label>
                <Input
                  id="expiryDate"
                  type="date"
                  value={formData.expiryDate}
                  onChange={(e) => setFormData({ ...formData, expiryDate: e.target.value })}
                  min={formData.effectiveDate || undefined}
                  disabled={!formData.effectiveDate}
                  className={errors.expiryDate ? "border-red-500" : ""}
                />
                {errors.expiryDate && <p className="text-sm text-red-500">{errors.expiryDate}</p>}
              </div>

              <div className="space-y-2">
                <Label htmlFor="approvedBy">Approved By</Label>
                <Select value={formData.approvedBy} onValueChange={(value) => setFormData({ ...formData, approvedBy: value })}>
                  <SelectTrigger className={errors.approvedBy ? "border-red-500" : ""}>
                    <SelectValue placeholder="Select approver" />
                  </SelectTrigger>
                  <SelectContent>
                    {approvers.map((user) => (
                      <SelectItem key={user.id} value={user.id}>
                        {user.name} - {user.userRole}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
                {errors.approvedBy && <p className="text-sm text-red-500">{errors.approvedBy}</p>}
                <p className="text-sm text-muted-foreground">Required for published memos</p>
              </div>

              <div className="flex items-center space-x-2">
                <input
                  type="checkbox"
                  id="requireSignature"
                  checked={formData.requireSignature}
                  onChange={(e) => setFormData({ ...formData, requireSignature: e.target.checked })}
                  className="rounded border-gray-300"
                />
                <Label htmlFor="requireSignature">Require digital signature from users</Label>
              </div>
            </CardContent>
          </Card>

          {/* Content Section */}
          <Card>
            <CardHeader>
              <CardTitle>Memo Content *</CardTitle>
            </CardHeader>
            <CardContent>
              <Textarea
                id="content"
                value={content}
                onChange={(e) => setContent(e.target.value)}
                placeholder="Enter the detailed memo content..."
                className={`min-h-[300px] ${errors.content ? "border-red-500" : ""}`}
              />
              {errors.content && <p className="text-sm text-red-500">{errors.content}</p>}
            </CardContent>
          </Card>
        </div>

        {/* Sidebar */}
        <div className="space-y-6">
          {/* AI Enhancement Preview */}
          {aiState.showPreview && (
            <Card className="border-purple-200 bg-purple-50">
              <CardHeader>
                <CardTitle className="text-sm text-purple-800">
                  AI Enhancement Preview
                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-3">
                <div className="text-sm text-purple-700">
                  <p className="font-medium mb-2">Enhanced {aiState.currentField}:</p>
                  <div className="bg-white p-3 rounded border max-h-40 overflow-y-auto">
                    {aiState.currentField === 'content' ? (
                      <pre className="whitespace-pre-wrap text-xs">{aiState.result}</pre>
                    ) : (
                      <p className="text-xs">{aiState.result}</p>
                    )}
                  </div>
                </div>
                <div className="flex gap-2">
                  <Button size="sm" onClick={handleAiAccept} className="flex-1">
                    Accept
                  </Button>
                  <Button size="sm" variant="outline" onClick={handleAiCancel} className="flex-1">
                    Cancel
                  </Button>
                </div>
              </CardContent>
            </Card>
          )}

          {/* Help Card */}
          <Card>
            <CardHeader>
              <CardTitle className="text-sm">Quick Tips</CardTitle>
            </CardHeader>
            <CardContent className="text-sm space-y-2">
              <p><strong>Save as Draft:</strong> Save changes without publishing. Only drafts can be edited.</p>
              <p><strong>Save & Publish:</strong> Make the memo active and available for assignments.</p>
              <p><strong>Active Memos:</strong> Cannot be edited. Use duplicate to create a new version.</p>
            </CardContent>
          </Card>

          {/* Memo Info */}
          <Card>
            <CardHeader>
              <CardTitle className="text-sm">Memo Information</CardTitle>
            </CardHeader>
            <CardContent className="text-sm space-y-2">
              <p><strong>Number:</strong> {memo.number}</p>
              <p><strong>Version:</strong> {memo.version || 1}</p>
              <p><strong>Status:</strong> {memo.status}</p>
              <p><strong>Created:</strong> {new Date(memo.createdAt).toLocaleDateString()}</p>
              <p><strong>Modified:</strong> {new Date(memo.updatedAt).toLocaleDateString()}</p>
            </CardContent>
          </Card>
        </div>
      </div>
    </div>
  );
}
